import { EXCEPTION_TYPE, getExceptionTypeLabel } from 'settings/constants/exceptionType'
import T from 'T'

// RHF field names for the exception detail form. Most per-type sub-values funnel through
// `description` (Weight value, Serial number, Special tile, No Access sub-option); Damage
// uses `responsibleParty`; Set location uses `latitude`/`longitude`. `workOrderContainerId`
// scopes a new exception to a specific container ('' = WO-level).
export const EXCEPTION_FORM_FIELD = {
  EXCEPTION_TYPE: 'exceptionType',
  NOTE_TEXT: 'noteText',
  DESCRIPTION: 'description',
  LATITUDE: 'latitude',
  LONGITUDE: 'longitude',
  RESPONSIBLE_PARTY: 'responsibleParty',
  DELAY_MS: 'delayMs',
  WORK_ORDER_CONTAINER_ID: 'workOrderContainerId',
} as const

// Sentinel for the WO-level option in the "Exception for" selector. MUI Select renders an
// empty input for value '' (even though the option shows checked), so the WO choice needs a
// real value; it maps back to null on the create payload.
export const EXCEPTION_TARGET_WORK_ORDER = 'WORK_ORDER'

export interface ExceptionFormValues {
  exceptionType: string
  noteText: string
  description: string
  latitude: string
  longitude: string
  responsibleParty: string
  // Elapsed stopwatch milliseconds; persisted through `description` for Delay (the DTOs
  // carry no delay field — see getExceptionDelayMs in utils/exceptionFields).
  delayMs: string
  workOrderContainerId: string
}

// Options for the "Exception type" selector, derived from the registry so the list stays in
// sync with the backend WorkOrderExceptionType enum. Sign is excluded per product: the web no
// longer creates Signature exceptions (legacy mobile ones still render via the registry).
export const EXCEPTION_TYPE_OPTIONS = Object.values(EXCEPTION_TYPE)
  .filter(value => value !== EXCEPTION_TYPE.SIGN)
  .map(value => ({
    key: value,
    value,
    label: getExceptionTypeLabel(value),
  }))

export const NO_ACCESS_OPTIONS = [T.BLOCKED, T.NOT_OUT, T.LOCKED]
// Damage splits into two single-selects: the damaged thing (stored in `description`)
// and who is responsible (stored in `responsibleParty`).
export const DAMAGE_TYPE_OPTIONS = [T.CONTAINER, T.PROPERTY]
export const DAMAGE_RESPONSIBLE_OPTIONS = [T.CUSTOMER, T.COMPANY]

// Special. Stored in `description` (no BE enum; mobile writes the same string to
// metadata.selected_special).
export const SPECIAL_OPTIONS = [
  { value: T.DIGOUT, label: T.DIGOUT },
  { value: T.TIRE, label: T.TIRE },
  { value: T.LITTER, label: T.LITTER },
  { value: T.SPECIAL, label: T.SPECIAL },
]

// Types whose sub-value is a single free-text/number value stored in `description`,
// mapped to the label the detail field should show.
export const DESCRIPTION_FIELD_LABEL: Record<string, string> = {
  [EXCEPTION_TYPE.WEIGHT]: T.WEIGHT_LBS,
  [EXCEPTION_TYPE.SERIAL_NUMBER]: T.SERIAL_NUMBER,
  [EXCEPTION_TYPE.EXTRA_ITEM]: T.NUMBER_OF_EXTRAS,
}

// Description-channel types whose value is a number — the field renders as a numeric input.
export const NUMERIC_DESCRIPTION_TYPES: ReadonlySet<string> = new Set([EXCEPTION_TYPE.WEIGHT, EXCEPTION_TYPE.EXTRA_ITEM])
